import { CommonModule } from '@angular/common';
import { booleanAttribute, Component, inject, input, model, OnInit, signal, TemplateRef } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { catchError, debounceTime, forkJoin, map, Observable, of, Subject } from 'rxjs';

import { BaseComponent } from '@base/base.component';
import { DefaultFilter, filterTreeArray, Guid, makeObjectsTree, ObjectOperation, ObjectType, SM, sortStringsBy, stopEvent, SYSTEM_USER_ID, Tag, traverseTreeArray, TreeItem, VzTag } from '@models';
import { HttpApiError } from '@models/api/http-api-error';
import { ApiService } from '@services/api.service';
import { TagDrawerComponent } from '@shared/_drawers/tag/tag.component';
import { ErrorMessageComponent } from '@shared/_utils/error-message.component';
import { DmTreeComponent } from '@shared/_views/dm-tree';
import { ProjectBadgeComponent } from '@shared/_views/project-badge.component';
import { TagComponent } from '@shared/_views/tag/tag.component';
// import { HelpersService } from '@services/helpers.service';
import { IObjectEvent, StompService } from '@services/stomp.service';
import { StoreService } from '@services/store.service';
import { AutoFocusDirective } from '@shared/_utils/autofocus.component';
import { ErrorPanelComponent } from '@shared/_utils/error-panel';
import { CacheService } from '@services/cache.service';

@Component({
    selector: 'vz-tags-tree',
    templateUrl: './tags-tree.component.html',
    styleUrl: './tags-tree.component.less',
    imports: [
        CommonModule, FormsModule,
        NzButtonModule, NzCheckboxModule, NzSwitchModule, NzInputModule, NzPopconfirmModule,
        ErrorMessageComponent, ErrorPanelComponent, TagDrawerComponent, TagComponent, DmTreeComponent, ProjectBadgeComponent, AutoFocusDirective
    ],
})
@Tag('TagsTreeComponent')
export class TagsTreeComponent extends BaseComponent implements OnInit {
    _hostLayout = false;

    projectId       = input<Guid>();
    mode            = input<'normal' | 'select' | 'multiselect'>('normal');
    showOrgTags     = input(false, { transform: booleanAttribute });
    canEditOrg      = input(false, { transform: booleanAttribute });
    canEditProj     = input(false, { transform: booleanAttribute });
    noHeader        = input(false, { transform: booleanAttribute });
    noCreateButton  = input(false, { transform: booleanAttribute });
    noEditToggle    = input(false, { transform: booleanAttribute });
    showAllProjects = input(false, { transform: booleanAttribute });
    noAutofocus     = input(false, { transform: booleanAttribute });
    emptyTpl        = input<TemplateRef<any>>();

    selected        = model<string[]>();

    items           = signal<TreeItem<VzTag>[] | undefined>(undefined);
    searchItems     = signal<TreeItem<VzTag>[] | undefined>(undefined);
    tag             = signal<VzTag | undefined>(undefined);
    pTag            = signal<VzTag | undefined>(undefined);
    showEdits       = signal<boolean>(false);
    disabledGroups  = signal<SM<boolean>>({});
    fText           = signal<string>('');

    checked: SM<boolean> = {};

    filter = new DefaultFilter('tags', ['text', 'description'], true);
    search = new Subject<string>();
    loadChildren = (tag: VzTag) => this.loadTagChildren(tag);
    isChecked = (tag: VzTag) => tag?.id && this.checked[tag.id] ? 'tt-checked' : undefined;

    SUI = SYSTEM_USER_ID;
    se = stopEvent;

    readonly _cache     = inject(CacheService);

    constructor(
        protected _store: StoreService,
        private _api: ApiService,
        private _stomp: StompService,
    ) {
        super(_store);
        this._S.objectEvent = this._stomp.onObjectEvent.subscribe(e => this.handleObjectEvent(e));
        this._S.search = this.search.pipe(debounceTime(300)).subscribe(v => this.makeSearch(v));
    }

    ngOnInit(): void {
        this.initData();
    }

    vzOnActiveOrgChange(): void {
        this.initData();
    }

    initData(): void {
        this.checked = {};
        this._L('initData','showAllProjects:', this.showAllProjects());
        const req = this.showAllProjects()
            ? of(this._store.getState('tags')).pipe(map(st => Object.values(st.items).filter(item => item.orgId == this.activeOrgId && !item.parentId)))
            : this.showOrgTags() && this.projectId()
                ? forkJoin([this._api.getTagsForOrg(this.activeOrgId!), this._api.getTagsForOrg(this.activeOrgId!, undefined, this.projectId())]).pipe(map(t => [...t[0], ...t[1]]))
                : this._api.getTagsForOrg(this.activeOrgId!, undefined, this.projectId());

        req.subscribe({
            next: rootTags => {
                const selected = this.selected();
                if (selected?.length) {
                    this._cache.getArray('tag', selected).subscribe(sts => {
                    // forkJoin(selected.map(id => this._cache.get('tag', id))).subscribe(sts => {
                        const roots: SM<boolean> = {};
                        sts?.forEach(st => {
                            if (st.item?.id) {
                                this.checked[st.item.id] = true;
                            }
                            const root = st.item?.parents?.find(t => !t.parentId);
                            if (root?.id) {
                                roots[root.id] = true;
                            }
                        });
                        const selectedItems = makeObjectsTree([...this.getAllChildren(Object.keys(roots)), ...rootTags.filter(t => !roots[t.id!])].map(t => new VzTag(t)), sortStringsBy('text'));
                        this.items.set(selectedItems.sort(sortStringsBy('text')));
                        this.updateDisabledGroups();
                    });
                }
                else {
                    this.items.set(rootTags.sort(sortStringsBy('text')));
                    this.updateDisabledGroups();
                }
            },
            error: err => this._error.set(err)
        });
    }

    editTag(tag?: VzTag, newSubtag = false): void {
        if (tag) {
            if (newSubtag) {
                this.tag.set(new VzTag());
                this.pTag.set(new VzTag(tag));
            }
            else {
                this.tag.set(tag);
                this.pTag.set(new VzTag({ id: tag.parentId, projectId: tag.projectId }));
            }
        }
        else {
            this.tag.set(new VzTag());
            this.pTag.set(undefined);
        }
    }

    deleteItem(id: Guid): void {
        this._api.deleteTag(id).subscribe({
            error: err => this.__error(err)
        });
    }

    closeTagDrawer(tag?: VzTag) {
        this.tag.set(undefined);
        if (tag) {
            const items = this.items();
            if (items) {
                traverseTreeArray(items, 'children', t => {
                    if (t.id == tag.id) {
                        t.parse(tag);
                        return true;
                    }
                    return;
                });
                this.items.set([...items]);
                this.updateDisabledGroups();
                // this.__cdr?.markForCheck();
            }
        }
    }

    loadTagChildren(tag: VzTag): Observable<VzTag[]> {
        return this._api.getTagsForOrg(this.activeOrgId!, tag.id, tag.projectId).pipe(
            catchError(error => {
                const err: HttpApiError = error instanceof HttpApiError ? error : new HttpApiError('Ошибка загрузки тегов.', error);
                this.__error(err);
                return of([]);
            }),
            map(items => items.sort(sortStringsBy('text')))
        );
    }

    handleObjectEvent(e: IObjectEvent): void {
        if (e.type == ObjectType.Tag && e.ids) {
            if (e.operation == ObjectOperation.Create) {
                this._cache.getArray('tag', e.ids).subscribe(sts => this.updateTreeWithNewTags(sts.map(x => x.item).filter(x => !!x)));
                // forkJoin(e.ids.map(id => this._cache.get('tag', id))).subscribe(sts => this.updateTreeWithNewTags(sts.map(x => x.item).filter(x => !!x)));
            }
            else if (e.operation == ObjectOperation.Delete && this.items()?.length) {
                this.items.set(filterTreeArray<TreeItem<VzTag>>(this.items() || [], 'children', t => !t.id || !e.ids.includes(t.id)));
            }
        }
    }

    updateTreeWithNewTags(tags?: (VzTag | undefined)[]): void {
        if (tags && tags.length) {
            const items: SM<VzTag[]> = {};
            tags?.forEach(tag => {
                if (tag && (((!this.projectId() || this.showOrgTags()) && !tag.projectId) || (this.projectId() == tag.projectId))) {
                    const pid = tag.parentId || '_';
                    if (!items[pid]) {
                        items[pid] = [tag];
                    }
                    else {
                        items[pid].push(tag);
                    }
                }
            });
            const root = { children: this.items() || [] };
            this.updateTree('_', root, items);
            this.items.set([...root.children]);
        }
    }

    updateTree(pid: string | undefined, root: { children?: TreeItem<VzTag>[] }, items: SM<VzTag[]>): void {
        if (Object.keys(items).length == 0 || !pid) {
            return;
        }
        if (root.children) {
            if (items[pid]) {
                root.children.push(...items[pid]);
                delete items[pid];
            }
            for (const ch of root.children) {
                if (ch.children) {
                    this.updateTree(ch.id, ch, items)
                }
            }
            root.children.sort(sortStringsBy('text'));
        }
    }

    makeSearch(text: string): void {
        this._L('makeSearch', text);
        this.__U(['lastSearch']);
        if (text) {
            this._S.lastSearch = of(this._store.getState('tags')).pipe(
                map(st => Object.values(st.items).filter(
                    item => item.orgId == this.activeOrgId
                        && (
                            this.showAllProjects()
                            || ((!this.projectId() || this.showOrgTags()) && !item.projectId)
                            || (this.projectId() && this.projectId() == item.projectId)
                        )
                        && this.filter.match(item, this.filter.parse(text)))
                ),
            ).subscribe({
                next: items => {
                    const searchTree = makeObjectsTree(items.map(t => new VzTag(t)), sortStringsBy('text'));
                    this.searchItems.set(searchTree);
                },
                error: err => this.__error(err)
            });
        }
        else {
            this.searchItems.set(undefined);
        }
    }

    tagClick(tag?: VzTag): void {
        if (!tag || !tag.id) {
            return;
        }
        if (this.mode() == 'normal') {
            if ((tag.projectId && this.canEditProj()) || (!tag.projectId && this.canEditOrg())) {
                this.editTag(tag);
            }
        }
        else if (this.mode() == 'select') {
            this.selected.set([tag.id]);
        }
        else if (this.mode() == 'multiselect') {
            const dg = this.disabledGroups();
            if (!this.checked[tag.id] && dg && (dg[tag.id] || (tag.parentId && dg[tag.parentId]))) {
                return;
            }
            this.checked[tag.id] = !this.checked[tag.id];
            this.updateSelected();
            this.updateDisabledGroups();
            this.__detectChanges();
        }
    }

    updateSelected(): void {
        this.selected.set(Object.keys(this.checked).filter(id => this.checked[id]));
    }

    getAllChildren(ids: Guid[]): VzTag[] {
        const res: VzTag[] = [];
        if (ids && ids.length) {
            const tagsMap = this._store.getState('tags').items;
            const tags = Object.values(tagsMap);
            let c = 100000;
            while (ids.length && c-- > 0) {
                const id = ids.pop();
                if (id) {
                    if (tagsMap[id]) {
                        res.push(tagsMap[id]);
                    }
                    ids.push(...tags.filter(t => t.parentId == id).map(t => t.id!));
                }
            }
        }
        return res;
    }

    updateDisabledGroups(): void {
        const selected = this.selected();
        const tagsMap = this._store.getState('tags').items;
        if (selected && selected.length) {
            const groups: SM<boolean> = {};
            for (const id of selected) {
                const tag = tagsMap[id];
                if (tag) {
                    if (tag.id && tag.uniqueGroup) {
                        groups[tag.id] = true;
                    }
                    if (tag.parentId && tagsMap[tag.parentId] && tagsMap[tag.parentId].uniqueGroup) {
                        groups[tag.parentId] = true;
                    }
                }
            }
            this.disabledGroups.set(groups);
        }
        else {
            const groups = this.disabledGroups();
            if (!groups || Object.keys(groups).length > 0) {
                this.disabledGroups.set(groups);
            }
        }
    }

}
